import { supabase } from '@/utils/supabase';
import type {
  OfficerMetrics,
  RecruiterMetrics,
  DocumentProcessingMetrics,
  GPUStats,
  ExtractionHistoryItem,
  ExtractionHistoryParams
} from '@/components/wiseboard/types/wiseboard';
import { createComponentLogger } from '@/utils/logger';
import type { RealtimeChannel, RealtimePostgresChangesPayload } from '@supabase/supabase-js';

const log = createComponentLogger('WiseboardService', 'wiseboard');

export interface DateRange {
  start: Date;
  end: Date;
}

export const wiseboardService = {
  /**
   * Subscribes to real-time changes on the pre_approvals table
   * Returns the channel so it can be unsubscribed later
   */
  subscribeToPreApprovals(
    callback: (payload: RealtimePostgresChangesPayload<Record<string, unknown>>) => void
  ): RealtimeChannel {
    log.info('Setting up real-time subscription for pre_approvals');

    const channel = supabase
      .channel('wiseboard-preapprovals')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'pre_approvals'
        },
        (payload) => {
          log.info('Pre-approval change detected', {
            eventType: payload.eventType,
            recordId: (payload.new as Record<string, unknown>)?.id ?? (payload.old as Record<string, unknown>)?.id
          });
          callback(payload);
        }
      )
      .subscribe((status) => {
        log.info('Wiseboard subscription status', { status });
      });

    return channel;
  },

  /**
   * Fetches metrics grouped by loan officer
   */
  async getPreApprovalByOfficer(dateRange: DateRange): Promise<{ data: OfficerMetrics[]; error: Error | null }> {
    try {
      log.info('Fetching pre-approval by officer');

      const { data, error } = await supabase.rpc('get_preapproval_by_officer', {
        start_date: dateRange.start.toISOString(),
        end_date: dateRange.end.toISOString()
      });

      if (error) {
        log.error('Failed to fetch pre-approval by officer', { error: error.message });
        return { data: [], error: new Error(error.message) };
      }

      return { data: (data as OfficerMetrics[]) ?? [], error: null };
    } catch (err) {
      const error = err as Error;
      log.error('Exception fetching pre-approval by officer', { error: error.message });
      return { data: [], error };
    }
  },

  /**
   * Fetches recruitment metrics grouped by recruiter
   */
  async getRecruitmentByRecruiter(dateRange: DateRange): Promise<{ data: RecruiterMetrics[]; error: Error | null }> {
    try {
      log.info('Fetching recruitment by recruiter');

      const { data, error } = await supabase.rpc('get_recruitment_by_recruiter', {
        start_date: dateRange.start.toISOString(),
        end_date: dateRange.end.toISOString()
      });

      if (error) {
        log.error('Failed to fetch recruitment by recruiter', { error: error.message });
        return { data: [], error: new Error(error.message) };
      }

      return { data: (data as RecruiterMetrics[]) ?? [], error: null };
    } catch (err) {
      const error = err as Error;
      log.error('Exception fetching recruitment by recruiter', { error: error.message });
      return { data: [], error };
    }
  },

  /**
   * Fetches document processing metrics grouped by user
   */
  async getDocumentProcessingByUser(dateRange: DateRange): Promise<{ data: DocumentProcessingMetrics[]; error: Error | null }> {
    try {
      log.info('Fetching document processing by user');

      const { data, error } = await supabase.rpc('get_document_processing_by_user', {
        start_date: dateRange.start.toISOString(),
        end_date: dateRange.end.toISOString()
      });

      if (error) {
        log.error('Failed to fetch document processing by user', { error: error.message });
        return { data: [], error: new Error(error.message) };
      }

      return { data: (data as DocumentProcessingMetrics[]) ?? [], error: null };
    } catch (err) {
      const error = err as Error;
      log.error('Exception fetching document processing by user', { error: error.message });
      return { data: [], error };
    }
  },

  /**
   * Fetches GPU processing statistics (all-time totals, no date range)
   */
  async getGPUProcessingStats(): Promise<{ data: GPUStats[]; error: Error | null }> {
    try {
      log.info('Fetching GPU processing stats');

      const { data, error } = await supabase.rpc('get_gpu_processing_stats');

      if (error) {
        log.error('Failed to fetch GPU processing stats', { error: error.message });
        return { data: [], error: new Error(error.message) };
      }

      log.info('GPU processing stats fetched successfully', { gpuCount: (data as GPUStats[])?.length ?? 0 });
      return { data: (data as GPUStats[]) ?? [], error: null };
    } catch (err) {
      const error = err as Error;
      log.error('Exception fetching GPU processing stats', { error: error.message });
      return { data: [], error };
    }
  },

  /**
   * Fetches document extraction history with filtering and pagination
   */
  async getExtractionHistory(params: ExtractionHistoryParams): Promise<{ data: ExtractionHistoryItem[]; error: Error | null }> {
    try {
      log.info('Fetching extraction history', {
        startDate: params.startDate,
        endDate: params.endDate,
        status: params.status,
        gpu: params.gpu,
        search: params.search,
        limit: params.limit,
        offset: params.offset
      });

      const { data, error } = await supabase.rpc('get_extraction_history', {
        p_organization_id: params.organizationId ?? null,
        p_start_date: params.startDate,
        p_end_date: params.endDate,
        p_status: params.status ?? null,
        p_gpu: params.gpu ?? null,
        p_search: params.search ?? null,
        p_limit: params.limit,
        p_offset: params.offset
      });

      if (error) {
        log.error('Failed to fetch extraction history', { error: error.message });
        return { data: [], error: new Error(error.message) };
      }

      log.info('Extraction history fetched successfully', { count: (data as ExtractionHistoryItem[])?.length ?? 0 });
      return { data: (data as ExtractionHistoryItem[]) ?? [], error: null };
    } catch (err) {
      const error = err as Error;
      log.error('Exception fetching extraction history', { error: error.message });
      return { data: [], error };
    }
  },

  /**
   * Subscribes to real-time changes on the document_processing_queue table
   * Returns the channel so it can be unsubscribed later
   */
  subscribeToDocumentQueue(
    callback: (payload: RealtimePostgresChangesPayload<Record<string, unknown>>) => void
  ): RealtimeChannel {
    log.info('Setting up real-time subscription for document_processing_queue');

    const channel = supabase
      .channel('wiseboard-document-queue')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'document_processing_queue'
        },
        (payload) => {
          log.info('Document queue change detected', {
            eventType: payload.eventType,
            recordId: (payload.new as Record<string, unknown>)?.id ?? (payload.old as Record<string, unknown>)?.id
          });
          callback(payload);
        }
      )
      .subscribe((status) => {
        log.info('Document queue subscription status', { status });
      });

    return channel;
  },

  /**
   * Fetches all rendered Wiseboard data in parallel
   */
  async fetchAllData(dateRange: DateRange): Promise<{
    byOfficer: OfficerMetrics[];
    byRecruiter: RecruiterMetrics[];
    byDocumentUser: DocumentProcessingMetrics[];
    error: Error | null;
  }> {
    const [byOfficerResult, byRecruiterResult, byDocumentUserResult] = await Promise.all([
      this.getPreApprovalByOfficer(dateRange),
      this.getRecruitmentByRecruiter(dateRange),
      this.getDocumentProcessingByUser(dateRange)
    ]);

    const error = byOfficerResult.error || byRecruiterResult.error || byDocumentUserResult.error;

    return {
      byOfficer: byOfficerResult.data,
      byRecruiter: byRecruiterResult.data,
      byDocumentUser: byDocumentUserResult.data,
      error
    };
  }
};

export default wiseboardService;
